// Can a staging table tell what has changed since the last push to ClientVerse?
// Without that, every load re-sends every row, which is the API-call cost this
// report exists to catch. Judged from column names alone, so a table that
// tracks change some other way (CDC, a side table) shows as untracked until its
// schema says so.
//
// Levels, best first:
//   tracked     — a per-row push record: when or what was last sent
//                 (cv_synced_at, last_pushed_hash, sync_status, …). The load can
//                 select exactly the rows that need sending.
//   detectable  — a content hash or a source change marker (stg_hash,
//                 source_modified_date, ia_change_dt, stg_updated_at, …).
//                 Changed rows can be found against a stored watermark or hash,
//                 but the table itself doesn't record what was sent.
//   load-only   — only staging load stamps (stg_loaded_at, stg_batch_id). A full
//                 reload stamps every row, so nothing narrows the push.
//   none        — no tracking columns at all.

export type TrackingLevel = 'tracked' | 'detectable' | 'load-only' | 'none';

export interface TableTracking {
  table: string;
  level: TrackingLevel;
  /** Columns recording what was last pushed. */
  pushRecord: string[];
  /** Content hash columns. */
  hash: string[];
  /** Columns that move when the source row changes. */
  changeMarkers: string[];
  /** Staging load stamps. */
  loadStamps: string[];
  /** ClientVerse record ids written back (sf_*_id): insert vs update is known. */
  writeBack: string[];
  /** Soft-delete columns, so a deletion can be pushed too. */
  deletes: string[];
}

const PUSH_RECORD = [
  /^(cv|sf)_(synced|pushed|sync|push|last_sync|last_push)(_at|_dt|_date|_on|_status|_hash)?$/,
  /^(last_)?(synced|pushed)_(at|dt|date|on|hash)$/,
  /^(sync|push)_(status|state|hash|at|dt)$/,
  /^last_(sync|push)(_at|_dt|_date|_hash)?$/,
];
const HASH = [/^(stg_|row_|record_)?hash$/, /_hash$/, /checksum/];
const CHANGE_MARKER = [
  /^(source_|src_)?(last_)?(modified|updated|changed?)_(date|dt|at|on|time)$/,
  /^(source_|src_)?(date_)?(last_)?(modified|updated|changed)$/,
  /^(ia|source|src)_change_(dt|date|at)$/,
  /^stg_updated_at$/,
  /^(row_?version|rowversion|row_?timestamp)$/,
];
const LOAD_STAMP = [/^stg_(loaded_at|batch_id|load_id|run_id)$/, /^(batch|load|etl_run|run)_id$/, /^(loaded|staged)_at$/];
const WRITE_BACK = [/^sf_[a-z0-9_]+_id$/, /^sf_record_id$/];
const DELETES = [/^is_deleted$/, /^deleted(_at|_date|_dt|_flag)?$/, /^is_active$/];

const matches = (patterns: RegExp[], name: string) => patterns.some((p) => p.test(name));

export function classifyTable(table: string, fieldNames: readonly string[]): TableTracking {
  const pick = (patterns: RegExp[]) => fieldNames.filter((f) => matches(patterns, f.toLowerCase()));
  const pushRecord = pick(PUSH_RECORD);
  // A push-record hash (last_pushed_hash) is not also counted as a content hash.
  const hash = pick(HASH).filter((f) => !pushRecord.includes(f));
  const changeMarkers = pick(CHANGE_MARKER);
  const loadStamps = pick(LOAD_STAMP);
  const level: TrackingLevel =
    pushRecord.length > 0 ? 'tracked'
    : hash.length > 0 || changeMarkers.length > 0 ? 'detectable'
    : loadStamps.length > 0 ? 'load-only'
    : 'none';
  return { table, level, pushRecord, hash, changeMarkers, loadStamps, writeBack: pick(WRITE_BACK), deletes: pick(DELETES) };
}

export const LEVEL_LABEL: Record<TrackingLevel, string> = {
  tracked: 'Tracked',
  detectable: 'Change detectable',
  'load-only': 'Load stamps only',
  none: 'No tracking',
};

export const LEVEL_HINT: Record<TrackingLevel, string> = {
  tracked: 'Records what was last pushed, so the load sends only rows that changed since.',
  detectable: 'Has a hash or change marker, so changed rows can be found against a stored watermark, but nothing records what was sent.',
  'load-only': 'Only staging load stamps: a full reload stamps every row, so every row is pushed again.',
  none: 'No way to tell what changed: every row is pushed on every load.',
};

const LEVEL_ORDER: TrackingLevel[] = ['none', 'load-only', 'detectable', 'tracked'];

/** Worst first, then by name: the tables that need work come to the top. */
export function sortByNeed(rows: TableTracking[]): TableTracking[] {
  return [...rows].sort((a, b) => LEVEL_ORDER.indexOf(a.level) - LEVEL_ORDER.indexOf(b.level) || a.table.localeCompare(b.table));
}

export function trackingCsv(rows: TableTracking[]): string {
  const esc = (s: string) => (/[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const lines = ['table,level,push_record,hash,change_markers,load_stamps,write_back,deletes'];
  for (const r of rows) {
    lines.push([
      r.table, LEVEL_LABEL[r.level], r.pushRecord.join(' '), r.hash.join(' '), r.changeMarkers.join(' '),
      r.loadStamps.join(' '), r.writeBack.join(' '), r.deletes.join(' '),
    ].map(esc).join(','));
  }
  return '﻿' + lines.join('\r\n') + '\r\n';
}
